import React, { useState, useMemo } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import './MatchingGame.css';

const levels = [
    {
      level: 1,
      title: 'Level 1: Basic Concepts',
      leftItems: [
        { id: 'local', text: 'Wallet prepares transaction locally' },
        { id: 'proof', text: 'Wallet proves payment without sharing history' },
        { id: 'verify', text: 'Chain checks a small proof instead of full data' },
        { id: 'private', text: 'Send a tip without revealing your balance' },
        { id: 'compute', text: 'Your device computes before sending to chain' }
      ],
      rightItems: [
        { id: 'on-chain', text: 'Zero-knowledge verification' },
        { id: 'privacy', text: 'Privacy protection' },
        { id: 'client-side', text: 'Client-side proving' },
        { id: 'zk-proof', text: 'Zero-knowledge proof' },
        { id: 'local-execution', text: 'Local execution' }
      ],
      correctMatches: {
        'local': 'client-side',
        'proof': 'zk-proof',
        'verify': 'on-chain',
        'private': 'privacy',
        'compute': 'local-execution'
      }
    },
    {
      level: 2,
      title: 'Level 2: Advanced Concepts',
      leftItems: [
        { id: 'witness', text: 'Your secret stays hidden' },
        { id: 'commitment', text: 'Lock value in cryptographic box' },
        { id: 'challenge', text: 'Verifier tests your proof' },
        { id: 'soundness', text: 'Cannot fake a false proof' },
        { id: 'completeness', text: 'True statements can be proven' },
        { id: 'zero-knowledge', text: 'Prove without revealing' },
        { id: 'stark', text: 'Scalable transparent proof system' },
        { id: 'merkle', text: 'Efficient data verification tree' }
      ],
      rightItems: [
        { id: 'hidden-secret', text: 'Hidden witness' },
        { id: 'crypto-box', text: 'Cryptographic commitment' },
        { id: 'verifier-test', text: 'Verification challenge' },
        { id: 'anti-fake', text: 'Soundness property' },
        { id: 'provable', text: 'Completeness property' },
        { id: 'private-proof', text: 'Zero-knowledge property' },
        { id: 'stark-system', text: 'STARK proof system' },
        { id: 'merkle-tree', text: 'Merkle tree structure' }
      ],
      correctMatches: {
        'witness': 'hidden-secret',
        'commitment': 'crypto-box',
        'challenge': 'verifier-test',
        'soundness': 'anti-fake',
        'completeness': 'provable',
        'zero-knowledge': 'private-proof',
        'stark': 'stark-system',
        'merkle': 'merkle-tree'
      }
    },
    {
      level: 3,
      title: 'Level 3: Miden-Specific Concepts',
      leftItems: [
        { id: 'note', text: 'Private payment container' },
        { id: 'account', text: 'Your private account state' },
        { id: 'stack', text: 'Stack-based computation' },
        { id: 'memory', text: 'Persistent memory storage' },
        { id: 'receipt', text: 'Proof of transaction' },
        { id: 'hash', text: 'Cryptographic hash function' }
      ],
      rightItems: [
        { id: 'payment-note', text: 'Payment note' },
        { id: 'account-state', text: 'Account state' },
        { id: 'stack-vm', text: 'Stack-based VM' },
        { id: 'memory-store', text: 'Memory storage' },
        { id: 'tx-receipt', text: 'Transaction receipt' },
        { id: 'hash-function', text: 'Hash function' }
      ],
      correctMatches: {
        'note': 'payment-note',
        'account': 'account-state',
        'stack': 'stack-vm',
        'memory': 'memory-store',
        'receipt': 'tx-receipt',
        'hash': 'hash-function'
      }
    },
    {
      level: 4,
      title: 'Level 4: Advanced Miden Features',
      leftItems: [
        { id: 'hybrid', text: 'Public and private combined' },
        { id: 'cond', text: 'Conditional execution' },
        { id: 'loop', text: 'Repeated operations' },
        { id: 'assert', text: 'Verify condition is true' },
        { id: 'merkle-proof', text: 'Prove membership in tree' },
        { id: 'batch', text: 'Process multiple at once' },
        { id: 'recursive', text: 'Proof within a proof' }
      ],
      rightItems: [
        { id: 'hybrid-note', text: 'Hybrid note' },
        { id: 'conditional', text: 'Conditional logic' },
        { id: 'iteration', text: 'Loop construct' },
        { id: 'assertion', text: 'Assert statement' },
        { id: 'merkle-membership', text: 'Merkle membership proof' },
        { id: 'batch-processing', text: 'Batch processing' },
        { id: 'recursive-proof', text: 'Recursive proof' }
      ],
      correctMatches: {
        'hybrid': 'hybrid-note',
        'cond': 'conditional',
        'loop': 'iteration',
        'assert': 'assertion',
        'merkle-proof': 'merkle-membership',
        'batch': 'batch-processing',
        'recursive': 'recursive-proof'
      }
    }
];

const MatchingGame = () => {
  const { progress, updateGameProgress } = useProgress();
  const gameId = 'matching-game';
  
  // Load saved progress
  const savedGame = progress.games[gameId];
  // level represents completed levels, so currentLevel should be the next level to play
  const completedLevels = savedGame?.level || 0;
  const nextLevelToPlay = Math.min(completedLevels + 1, levels.length);
  
  const [matches, setMatches] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(nextLevelToPlay);

  const currentLevelData = levels[currentLevel - 1];
  const correctMatches = currentLevelData.correctMatches;

  // Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Shuffle items when level changes
  const leftItems = useMemo(() => {
    return shuffleArray(levels[currentLevel - 1].leftItems);
  }, [currentLevel]);

  const rightItems = useMemo(() => {
    return shuffleArray(levels[currentLevel - 1].rightItems);
  }, [currentLevel]);

  const handleDragStart = (itemId) => {
    setDraggedItem(itemId);
  };

  const handleDrop = (rightItemId) => {
    if (!draggedItem) return;

    const isCorrect = correctMatches[draggedItem] === rightItemId;
    
    if (isCorrect) {
      setMatches(prev => {
        const newMatches = { ...prev, [draggedItem]: rightItemId };
        const newScore = Object.keys(newMatches).length;
        
        // Check if all matches are correct
        if (newScore === leftItems.length) {
          setCompleted(true);
          // Track progress - level completed
          const isFinalLevel = currentLevel === levels.length;
          updateGameProgress(gameId, newScore, currentLevel, isFinalLevel, 'Client-Side Proving');
        } else {
          // Track progress - score update
          updateGameProgress(gameId, newScore, currentLevel, false);
        }
        
        return newMatches;
      });
      setScore(prev => prev + 1);
    }
    
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const resetGame = () => {
    setMatches({});
    setScore(0);
    setCompleted(false);
    setDraggedItem(null);
  };

  const nextLevel = () => {
    if (currentLevel < levels.length) {
      const nextLevelNum = currentLevel + 1;
      setCurrentLevel(nextLevelNum);
      setMatches({});
      setScore(0);
      setCompleted(false);
      setDraggedItem(null);
      // Track progress - level unlocked (but not completed yet)
    
      updateGameProgress(gameId, 0, currentLevel, false);
    }
  };

  const resetToLevel1 = () => {
    setCurrentLevel(1);
    setMatches({});
    setScore(0);
    setCompleted(false);
    setDraggedItem(null);
    // Reset progress tracking
    updateGameProgress(gameId, 0, 1, false);
  };

  return (
    <div className="matching-game">
      <div className="game-header">
        <h2>{currentLevelData.title}</h2>
        <p>Drag each phrase on the left to its matching concept on the right</p>
        <div className="level-indicator">
          Level {currentLevel} of {levels.length}
        </div>
      </div>

      <div className="game-score">
        <div className="score-display">
          Score: {score} / {leftItems.length}
        </div>
        {completed && (
          <div className="completion-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            All matches correct! Great job!
          </div>
        )}
      </div>

      <div className="matching-container">
        <div className="left-column">
          <h3>What happens:</h3>
          {leftItems.map((item) => {
            const isMatched = matches[item.id];
            const isDragging = draggedItem === item.id;
            
            return (
              <div
                key={item.id}
                className={`match-item left-item ${isMatched ? 'matched' : ''} ${isDragging ? 'dragging' : ''}`}
                draggable={!isMatched}
                onDragStart={() => handleDragStart(item.id)}
              >
                {item.text}
                {isMatched && (
                  <span className="check-mark">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="right-column">
          <h3>Matches to:</h3>
          {rightItems.map((item) => {
            const isMatched = Object.values(matches).includes(item.id);
            
            return (
              <div
                key={item.id}
                className={`match-item right-item ${isMatched ? 'matched' : ''}`}
                onDrop={() => handleDrop(item.id)}
                onDragOver={handleDragOver}
              >
                {item.text}
                {isMatched && (
                  <span className="check-mark">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="game-actions">
        <button onClick={resetGame} className="reset-button">
          Reset
        </button>
        {completed && currentLevel < levels.length && (
          <button onClick={nextLevel} className="next-level-button">
            Next Level →
          </button>
        )}
        {completed && currentLevel === levels.length && (
          <button onClick={resetToLevel1} className="restart-button">
            Play Again
          </button>
        )}
      </div>

      <div className="game-explanation">
        <h4>What you're learning:</h4>
        <p>
          Miden uses client-side proving: your device does the computation locally, 
          creates a zero-knowledge proof, and the blockchain verifies it without seeing your data. 
          This keeps everything private while still being secure.
        </p>
      </div>
    </div>
  );
};

export default MatchingGame;

